import { useState } from "react";
import { BookOpen, Bus, ClipboardCheck, Utensils, Users, Shield, ArrowLeft, HelpCircle, Gamepad2 } from "lucide-react";

const GUIDES = [
  {
    title: "Camper Snapshot — Counselor",
    desc: "Your main dashboard. Attendance sync, buddy sheets, incident reports, messaging, hobbies, and more.",
    href: "/tutorialcounselor",
    icon: Users,
    color: "bg-sky-600",
    border: "border-sky-200",
    bg: "bg-sky-50",
    for: "Counselors",
  },
  {
    title: "Camper Snapshot — Supervisor",
    desc: "Division oversight. Conflict resolution, roster review, intramurals, escalation paths.",
    href: "/tutorialsupervisors",
    icon: Shield,
    color: "bg-emerald-700",
    border: "border-emerald-200",
    bg: "bg-emerald-50",
    for: "Supervisors",
  },
  {
    title: "Bus Mapping App",
    desc: "Mark campers present or absent as they board. rrdcbusmapping.com/counselor",
    href: "/tutorialbus",
    icon: Bus,
    color: "bg-blue-700",
    border: "border-blue-200",
    bg: "bg-blue-50",
    for: "Bus Counselors",
  },
  {
    title: "Attendance App",
    desc: "Daily group attendance. Login with Bunk Code + Name. rrdcattendance.com",
    href: "/tutorialattendance",
    icon: ClipboardCheck,
    color: "bg-indigo-700",
    border: "border-indigo-200",
    bg: "bg-indigo-50",
    for: "Counselors",
  },
  {
    title: "Lunch Ordering App",
    desc: "Order lunch for your group before 9:30 AM. rrdcdining.com",
    href: "/tutorialdining",
    icon: Utensils,
    color: "bg-amber-600",
    border: "border-amber-200",
    bg: "bg-amber-50",
    for: "Counselors",
  },
  {
    title: "Camp Games",
    desc: "RRDC Sports Arcade (6 games) + Lava Surfer. How to play each one.",
    href: "/tutorialgames",
    icon: Gamepad2,
    color: "bg-violet-700",
    border: "border-violet-200",
    bg: "bg-violet-50",
    for: "Everyone",
  },
];

export default function HelpPage() {
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? GUIDES : GUIDES.filter(g => g.for.toLowerCase().includes(filter));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="help-page">
      <header className="bg-gray-900 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Rolling River Day Camp</h1>
              <p className="text-xs text-gray-400">Staff Help & Training Guides</p>
            </div>
          </div>
          <a href="/login" className="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="back-to-login">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">How do I use this?</h2>
          <p className="text-base text-gray-600">Pick your guide below. Each one walks you through every screen, every button, step by step.</p>
        </div>

        {/* Filter */}
        <div className="flex justify-center gap-2 mb-8" data-testid="help-filter">
          {["all", "counselors", "supervisors", "bus"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f ? "bg-gray-900 text-white" : "bg-white border text-gray-600 hover:bg-gray-100"}`}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "All Guides" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Deadlines */}
        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-5 mb-8" data-testid="deadlines-box">
          <h3 className="text-lg font-bold text-red-900 mb-3">Daily Deadlines</h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl p-3 border border-red-200 text-center">
              <p className="text-2xl font-bold text-red-700">9:20 AM</p>
              <p className="text-xs text-red-600 mt-1">Buddy Sheets</p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-red-200 text-center">
              <p className="text-2xl font-bold text-red-700">9:30 AM</p>
              <p className="text-xs text-red-600 mt-1">Bus Attendance</p>
              <p className="text-[10px] text-gray-400">rrdcbusmapping.com/counselor</p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-red-200 text-center">
              <p className="text-2xl font-bold text-red-700">9:30 AM</p>
              <p className="text-xs text-red-600 mt-1">Lunch Orders</p>
              <p className="text-[10px] text-gray-400">rrdcdining.com</p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-red-200 text-center">
              <p className="text-2xl font-bold text-red-700">10:00 AM</p>
              <p className="text-xs text-red-600 mt-1">Group Attendance</p>
              <p className="text-[10px] text-gray-400">rrdcattendance.com</p>
            </div>
          </div>

          <h3 className="text-base font-bold text-red-900 mt-4 mb-2">Weekly Selection Windows</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-xl p-3 border border-blue-200 text-center">
              <p className="text-sm font-bold text-blue-800">Mariner Hobbies</p>
              <p className="text-xs text-green-700 font-semibold mt-1">Opens: Monday 12:00 AM</p>
              <p className="text-xs text-red-700 font-semibold">Closes: Tuesday 2:00 PM</p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-blue-200 text-center">
              <p className="text-sm font-bold text-blue-800">Navigator Hobbies</p>
              <p className="text-xs text-green-700 font-semibold mt-1">Opens: Monday 9:00 AM</p>
              <p className="text-xs text-red-700 font-semibold">Closes: Thursday 2:00 PM</p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-blue-200 text-center">
              <p className="text-sm font-bold text-blue-800">Navigator Intensives</p>
              <p className="text-xs text-green-700 font-semibold mt-1">Opens: Monday 9:00 AM</p>
              <p className="text-xs text-red-700 font-semibold">Closes: Tuesday 2:00 PM</p>
            </div>
          </div>
        </div>

        {/* Guide cards */}
        <div className="space-y-4">
          {filtered.map(g => {
            const Icon = g.icon;
            return (
              <a key={g.href} href={g.href} className={`block ${g.bg} border-2 ${g.border} rounded-2xl p-5 hover:shadow-md transition-shadow group`} data-testid={`guide-card-${g.href.slice(1)}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${g.color} text-white flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-900">{g.title}</h3>
                      <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{g.for}</span>
                    </div>
                    <p className="text-sm text-gray-600">{g.desc}</p>
                  </div>
                  <span className="text-gray-400 group-hover:text-gray-600 text-sm mt-1 flex-shrink-0">Read Guide &rarr;</span>
                </div>
              </a>
            );
          })}
        </div>

        {/* Tech support */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white border rounded-xl px-5 py-3">
            <HelpCircle className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">Something broken? Contact <strong>Brian Stein</strong></span>
          </div>
          <p className="text-xs text-gray-400 mt-3">Rolling River Day Camp — Camper Snapshot Platform</p>
        </div>
      </main>
    </div>
  );
}
